<template>
  <div
    :class="{
      'search-results': true,
      open: searchResultsOpen,
      'search-txt-unfocused': !searchHasFocus,
      'search-txt-focused': searchHasFocus,
      'two-columns':
        !mobileSearchOpen && (results.length > 0 || query.length > 0),
      'three-columns':
        !mobileSearchOpen && results.length == 0 && query.length == 0,
      mobile: mobileSearchOpen === true,
    }"
    role="alert"
  >
    <div
      class="categorized-links"
      role="region"
      aria-describedby="categorized-links"
    >
      <!-- Static non-search panel-->
      <div
        v-if="results.length == 0 && query.length == 0"
        class="categorized-links-section"
      >
        <h2 class="categorized-links-section-label">Trending Collections</h2>
        <ul class="collections">
          <li
            v-for="collection in trendingCollections.slice(0, 3)"
            :key="collection.slug"
            :data-product-id="collection.slug"
          >
            <nuxt-link
              :to="occassionUrl(collection.slug)"
              @click="hideSearch()"
            >
              <span class="name">{{ collection.name }}</span>
            </nuxt-link>
          </li>
        </ul>
        <div v-if="showSavedSearch">
          <h2 class="categorized-links-section-label">My Saved Searches</h2>
          <ul class="collections">
            <li v-for="search in savedSearches.slice(0, 3)" :key="search.id">
              <nuxt-link
                :to="`/store/?${search.query_string}`"
                @click="hideSearch()"
              >
                <span class="name">{{ search.name }}</span>
              </nuxt-link>
            </li>
          </ul>
        </div>
      </div>
      <!-- Search results are available list out all types of non products in left column-->
      <div v-else>
        <div v-if="searchCollections.length">
          <h2 class="categorized-links-section-label">Collections</h2>
          <ul class="collections">
            <li
              v-for="collection in searchCollections.slice(0, 3)"
              :key="collection.slug"
              :data-product-id="collection.slug"
            >
              <nuxt-link :to="collection.url" @click="hideSearch()">
                <span class="name">{{ collection.display_name }}</span>
              </nuxt-link>
            </li>
          </ul>
        </div>
        <div v-if="searchWineClubs.length">
          <h2 class="categorized-links-section-label">Wine Clubs</h2>
          <ul class="collections">
            <li
              v-for="wineclub in searchWineClubs.slice(0, 3)"
              :key="wineclub.slug"
              :data-product-id="wineclub.slug"
            >
              <nuxt-link :to="wineclub.url" @click="hideSearch()">
                <span class="name">{{ wineclub.display_name }}</span>
              </nuxt-link>
            </li>
          </ul>
        </div>
        <div v-if="searchLinks.length">
          <h2 class="categorized-links-section-label">Pages</h2>
          <ul class="collections">
            <li
              v-for="link in searchLinks.slice(0, 3)"
              :key="link.slug"
              :data-product-id="link.slug"
            >
              <nuxt-link :to="link.url" @click="hideSearch()">
                <span class="name">{{ link.display_name }}</span>
              </nuxt-link>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div>
      <SearchResults
        v-if="results.length > 0 || query.length > 0"
        :hide-search="hideSearch"
        :query="query"
        no-results-label="No Results"
        :results="results"
        :search-block-label="searchBlockLabelForQuery"
        :shop-all-label="viewAllLabel(count, { empty: 'Shop All' })"
        shop-all-link="/store/"
        :loading-flag="searchExecuting"
      />
      <div v-else>
        <SearchResults
          v-if="personalizedResults.length !== 0"
          :hide-search="hideSearch"
          no-results-label="No Results"
          :results="personalizedResults.slice(0, 3)"
          search-block-label="Recommended For You"
          :shop-all-label="viewAllLabel(personalizedTotalResults)"
          shop-all-link="/store/personalized/"
        />
        <SearchResults
          v-else
          :hide-search="hideSearch"
          no-results-label="No Results"
          :results="trendingResults.slice(0, 3)"
          search-block-label="Trending Wines"
          shop-all-link="/store/"
        />
      </div>
    </div>
    <SearchResults
      v-if="showLTO"
      :query="query"
      :hide-search="hideSearch"
      fixed-params="is_offer__in=true"
      no-results-label="No Results"
      :results="ltoResults.slice(0, 3)"
      search-block-label="Limited Time Offers"
      :shop-all-label="viewAllLabel(ltoTotalResults)"
      shop-all-link="/store/is_offer/true/"
      :loading-flag="ltoSearchExecuting"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'MegaSearch',
  components: {
    SearchResults: defineAsyncComponent(
      () => import('@/components/Header/SearchResults.vue'),
    ),
  },
  props: {
    hideSearch: {
      type: Function,
      required: true,
    },
    query: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      searchHasFocus: false,
      searchResultsPosition: 130,
      searchResultsOpen: true,
      personalizedResults: [],
      limitedTimeresults: [],
      trendingResults: [],
      trendingCollections: [],
      savedSearches: [],
      personalizedTotalResults: 0,
      ltoTotalResults: 0,
    }
  },
  computed: {
    ...mapGetters([
      'searchCollections',
      'searchWineClubs',
      'searchLinks',
      'count',
      'results',
      'searchExecuting',
      'ltoSearchExecuting',
      'mobileSearchOpen',
      'ltoResults',
      'ltoCount',
      'isSingleCollectionResults',
    ]),
    searchBlockLabelForQuery() {
      if (this.isSingleCollectionResults) {
        return `Results from ${this.searchCollections[0].display_name}`
      }
      return 'Search Results'
    },
    showSavedSearch() {
      return (
        this.$auth.loggedIn &&
        this.metadata.constance.S3_SAVED_SEARCH_ENABLED === 'True' &&
        this.savedSearches.length > 0
      )
    },
    showLTO() {
      return (
        this.ltoResults.length !== 0 &&
        this.results.length === 0 &&
        this.query.length === 0
      )
    },
  },
  mounted() {
    // If there is a query in the box during mount leave existing data intact
    if (this.query) return

    this.$axios.get('/api/catalog/empty-search/').then((resp) => {
      this.personalizedTotalResults = resp.data.personalized_count
      this.personalizedResults = resp.data.personalized_products.slice(0, 3)
      this.trendingCollections = resp.data.trending_collections.slice(0, 3)
      this.savedSearches = resp.data.saved_searches.slice(0, 3)
      this.ltoTotalResults = resp.data.highlighted_count
      this.$store.dispatch(
        'ltoSetSearchResults',
        resp.data.highlighted_offers.slice(0, 3),
      )
      // only look for trending wines if personalized is empty
      // theoretically this never happens unless the feature is turned off
      if (!this.personalizedResults.length) {
        this.$axios
          .get('/api/catalog/search/', {
            params: {
              ordering: '-popularity_ranking',
              ordering__in: '-popularity_ranking',
              page_size: 3,
              page: 1,
            },
          })
          .then((resp) => {
            this.trendingResults = resp.data.results
          })
      }
    })
  },
  created() {
    this.metadata = useMetadata()
  },
  methods: {
    occassionUrl(slug) {
      return `/store/occasion/${slug}`
    },
    viewAllLabel(amount, optional) {
      if (amount > 0) {
        return `View all ${amount} products`
      }
      return optional && optional.empty ? optional.empty : ''
    },
  },
}
</script>
